import { forwardRef, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useFileCompression } from './hooks/useFileCompression';
import { bytesToMb } from '../../@iosper/utils/Format';
import { Box, Button, Typography } from '@mui/material';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const FileUpload = forwardRef(
  (
    {
      accept = null,
      children,
      compressFile = false,
      onChangeUpload = null,
      maxFileSize = 2, // MB
      ...props
    },
    ref,
  ) => {
    const { outputFile, onHandleFileUpload, isLoading } = useFileCompression({
      compressFile: compressFile,
    }); // Hook con lógica de compresión de archivos

    const [error, setError] = useState(null);

    useEffect(() => {
      if (outputFile) handleOutputFile(outputFile);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outputFile]);

    const handleOutputFile = async (file) => {
      const validated = await validateFile(file);
      if (validated && onChangeUpload) {
        onChangeUpload(validated);
      }
    };

    const validateFile = async (file) => {
      if (!file) return null;

      // Validación de tipo de archivo (case-insensitive)
      if (accept) {
        const acceptedTypes = accept.split(',').map((type) => type.trim().toLowerCase());
        const fileType = file.type ? file.type.toLowerCase() : '';
        const fileName = file.name ? file.name.toLowerCase() : '';

        const isAccepted = acceptedTypes.some(
          (type) => fileType === type || (type.startsWith('.') && fileName.endsWith(type)),
        );

        if (!isAccepted) {
          setError('Por favor selecciona un archivo válido (pdf, jpg, png, etc).');
          return null;
        }
      }

      // Validación de tamaño
      if (bytesToMb(file.size) > maxFileSize) {
        setError('Superaste el límite de tamaño de archivos que podés cargar.');
        return null;
      }

      setError(null);
      return await getFileData(file);
    };

    const getFileData = (file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const fileExtension = file.name.split('.').pop();
          resolve({
            fileName: file.name,
            extension: '.' + fileExtension,
            dimension: file.size,
            contentBase64: reader.result.split(',')[1],
          });
        };
        reader.readAsDataURL(file);
      });
    };

    return (
      <Box sx={{ width: '100%' }}>
        <Button {...props} component='label' role={undefined}>
          {children}
          <VisuallyHiddenInput
            accept={accept}
            capture
            type='file'
            onChange={(e) => {
              onHandleFileUpload(e);
              e.target.value = ''; // <-- Limpia el valor para permitir subir el mismo archivo de nuevo
            }}
          />
        </Button>

        {error && (
          <Typography variant='caption' color='error' sx={{ mt: 1, display: 'block' }}>
            {error}
          </Typography>
        )}
      </Box>
    );
  },
);

export default FileUpload;
